/**
 * Bridges the OOUI world to chat client tools: stable tool naming, the
 * approval flow for mutating methods, and the world meta-tools. Port of
 * reactdashboard's CopilotRail/ooui/bridge.ts without zod.
 */

import { retryOnceAfterDelay, isObjectUnavailableError } from './world.js';

function slug(value) {
  return value
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 28)
    .toLowerCase();
}

function hash(value) {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36);
}

export function formatToolName(type, id, method) {
  return `ooui_${slug(type)}_${hash(id)}_${slug(method)}_${hash(method)}`;
}

const CONFIRM_TOOL = formatToolName('Dashboard', 'dashboard', 'confirmToolCall');
const GET_WORLD_TOOL = formatToolName('World', 'current', 'getCurrentWorld');
const INVOKE_TOOL = formatToolName('World', 'current', 'invokeObjectMethod');

function getWorldContext(world) {
  return {
    objects: Array.from(world.registry.values()).map(object => ({
      id: object.id,
      type: object.type,
      props: object.props,
      methods: Object.keys(object.methods),
    })),
  };
}

function executeWithAvailabilityRetry(execute) {
  return retryOnceAfterDelay(execute, isObjectUnavailableError, 4000);
}

/**
 * requestApproval({ summary, risk, tool, input }) must resolve to a boolean.
 */
export function createBridge(world, requestApproval) {
  const approvedActions = new Map();

  function getTools() {
    const objectTools = Array.from(world.registry.values()).flatMap(object =>
      Object.entries(object.methods).map(([method, definition]) => ({
        name: formatToolName(object.type, object.id, method),
        description: definition.description,
        parameters: definition.parameters,
      }))
    );

    return [
      ...objectTools,
      {
        name: CONFIRM_TOOL,
        description:
          'Ask the user to approve a proposed tool call, then execute it if approved. Required for methods marked as mutating; may also be used for any action that seems risky or consequential. The embedded tool call is executed atomically after approval.',
        parameters: {
          type: 'object',
          properties: {
            summary: { type: 'string', minLength: 1 },
            risk: { type: 'string' },
            tool: { type: 'string' },
            input: {},
            toolCall: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                tool: { type: 'string' },
                input: {},
              },
            },
          },
          required: ['summary'],
        },
      },
      {
        name: GET_WORLD_TOOL,
        description:
          'Read the current dashboard object world. Call this before interacting with newly opened controls.',
        parameters: { type: 'object', properties: {} },
      },
      {
        name: INVOKE_TOOL,
        description:
          'Invoke a method on an object discovered with getCurrentWorld. Use the object ID and method name exactly as returned.',
        parameters: {
          type: 'object',
          properties: {
            objectId: { type: 'string', minLength: 1 },
            method: { type: 'string', minLength: 1 },
            input: {},
          },
          required: ['objectId', 'method'],
        },
      },
    ];
  }

  function findObjectMethodByToolName(toolName) {
    for (const object of Array.from(world.registry.values())) {
      for (const method of Object.keys(object.methods)) {
        if (formatToolName(object.type, object.id, method) === toolName) {
          return { object, method };
        }
      }
    }
    return null;
  }

  async function execute(toolName, toolInput, approved) {
    if (toolName === CONFIRM_TOOL) {
      const request = toolInput || {};
      const targetTool =
        (request.toolCall && (request.toolCall.name || request.toolCall.tool)) ||
        request.tool;
      const targetInput =
        (request.toolCall && request.toolCall.input) || request.input || {};
      if (!request.summary || !targetTool) {
        throw new Error(
          'Approval requires summary and toolCall: { name, input } (or tool and input).'
        );
      }
      const actionKey = JSON.stringify([targetTool, targetInput]);
      if (approvedActions.has(actionKey)) {
        return {
          approved: true,
          executed: false,
          message:
            'This exact action was already approved and executed. Do not request approval again.',
          result: approvedActions.get(actionKey),
        };
      }
      const approvedByUser = await requestApproval({
        summary: request.summary,
        risk: request.risk,
        tool: targetTool,
        input: targetInput,
      });
      if (!approvedByUser) {
        return {
          approved: false,
          executed: false,
          message: 'The user did not approve this action.',
        };
      }
      const byDotted = Array.from(world.registry.values())
        .flatMap(object =>
          Object.keys(object.methods).map(method => ({ object, method }))
        )
        .find(({ object, method }) => `${object.id}.${method}` === targetTool);
      const result = byDotted
        ? await executeWithAvailabilityRetry(() =>
            world.execute(byDotted.object.id, byDotted.method, targetInput)
          )
        : await execute(targetTool, targetInput, true);
      approvedActions.set(actionKey, result);
      return { approved: true, executed: true, result };
    }

    if (toolName === GET_WORLD_TOOL) {
      return getWorldContext(world);
    }

    if (toolName === INVOKE_TOOL) {
      const request = toolInput || {};
      if (!request.objectId || !request.method) {
        throw new Error('invokeObjectMethod requires objectId and method');
      }
      const object = world.registry.get(request.objectId);
      const method = object && object.methods[request.method];
      if (method && method.mutates && !approved) {
        throw new Error(
          'This mutating action requires Dashboard.confirmToolCall approval.'
        );
      }
      return executeWithAvailabilityRetry(() =>
        world.execute(request.objectId, request.method, request.input || {})
      );
    }

    const found = findObjectMethodByToolName(toolName);
    if (found) {
      if (found.object.methods[found.method].mutates && !approved) {
        throw new Error(
          'This mutating action requires Dashboard.confirmToolCall approval.'
        );
      }
      return executeWithAvailabilityRetry(() =>
        world.execute(found.object.id, found.method, toolInput)
      );
    }

    return {
      message: `Tool "${toolName}" is unavailable because the dashboard changed. Use the available objects below and invoke them with the stable object-method invoker.`,
      availableObjects: getWorldContext(world).objects,
    };
  }

  return {
    getTools,
    getWorldContext: () => getWorldContext(world),
    handleToolCall: (name, input) => execute(name, input, false),
  };
}
